import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

const getErrorMessage = (error: any, fallbackMessage: string) => {
    const detail = error?.response?.data?.detail;
    if (detail) {
        return typeof detail === "string" ? detail : JSON.stringify(detail);
    }

    if (error?.message) {
        return error.message;
    }

    if (typeof error === "string") {
        return error;
    }

    if (error?.toString) {
        return error.toString();
    }

    return fallbackMessage;
};

export type CashReceiptMode = "R" | "W";

export interface CashReceiptResponse {
    payment_no: number;
    payment_loc?: string | null;
    payment_date?: string | null;
    /** Server-local "YYYY-MM-DDTHH:MM:SS" */
    payment_time?: string | null;
    payment_cashier_id?: string | null;
    payment_station?: string | null;
    payment_shift?: number | null;
    shift_log_id?: number | null;
    payment_description?: string | null;
    payment_amount?: number | null;
    payment_mode?: CashReceiptMode | string | null;
    created_at?: string | null;
}

/** Location, counter, shift, cashier and date/time are set by the server from the open shift. */
export type CashReceiptCreateRequest = {
    payment_mode: CashReceiptMode;
    payment_amount: number;
    payment_description?: string;
};

/** The cashier's open shift that a new entry is recorded against. */
export interface CashReceiptContext {
    shift_log_id: number;
    station_id: string;
    station_loc?: string | null;
    loc_desc?: string | null;
    shift_no: number;
    user_name: string;
    server_date: string;
}

export interface CashReceiptFilters {
    payment_loc?: string;
    payment_date?: string;
    payment_station?: string;
    payment_shift?: number;
    limit?: number;
}

export const getAllCashReceipts = async (filters?: CashReceiptFilters): Promise<CashReceiptResponse[]> => {
    try {
        const params = new URLSearchParams();
        if (filters) {
            if (filters.payment_loc) params.append("payment_loc", filters.payment_loc);
            if (filters.payment_date) params.append("payment_date", filters.payment_date);
            if (filters.payment_station) params.append("payment_station", filters.payment_station);
            if (filters.payment_shift !== undefined) params.append("payment_shift", String(filters.payment_shift));
            if (filters.limit !== undefined) params.append("limit", String(filters.limit));
        }
        
        const queryString = params.toString();
        const url = queryString ? `/pos_cash_rec_with/all?${queryString}` : "/pos_cash_rec_with/all";
        
        const response: ApiResponse<CashReceiptResponse[] | null> = (
            await apiClient.get(url, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.status === 401) {
            throw new Error("Not authorized to load cash receipts. Please sign in with backend authentication.");
        }

        throw new Error(getErrorMessage(error, "Failed to fetch cash receipts"));
    }
};

export const getCashReceiptContext = async (): Promise<CashReceiptContext | null> => {
    try {
        const response: ApiResponse<CashReceiptContext | null> = (
            await apiClient.get("/pos_cash_rec_with/context", {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data || null;
    } catch (error: any) {
        throw new Error(getErrorMessage(error, "Failed to check the open shift"));
    }
};

export const createCashReceipt = async (receiptData: CashReceiptCreateRequest): Promise<CashReceiptResponse> => {
    try {
        const response: ApiResponse<CashReceiptResponse> = (
            await apiClient.post("/pos_cash_rec_with/create", receiptData, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data;
    } catch (error: any) {
        const detail = error?.response?.data?.detail;
        // FastAPI validation errors arrive as a list of { loc, msg }
        if (Array.isArray(detail)) {
            throw new Error(detail.map((item: any) => item?.msg).filter(Boolean).join(" ") || "Invalid cash receipt.");
        }
        if (detail) {
            throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
        }

        throw new Error("Failed to create cash receipt");
    }
};

export const getCashReceiptById = async (receiptId: number): Promise<CashReceiptResponse | null> => {
    try {
        const response: ApiResponse<CashReceiptResponse | null> = (
            await apiClient.get(`/pos_cash_rec_with/${receiptId}`, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data || null;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            const detail = error.response.data.detail;
            const message = typeof detail === 'string' ? detail : JSON.stringify(detail);
            throw new Error(message);
        }

        throw new Error("Failed to fetch cash receipt");
    }
};